import React, { useEffect, useState, useRef } from "react";
import { driveThumb, drivePreview, bunnyEmbed } from "../data/categoriesData";
import useBackButtonClose from "../hooks/useBackButtonClose";

const PHOTO = "photos";
const VIDEO = "videos";

const MOMENTA_WORDMARK =
  "https://customer-assets.emergentagent.com/job_momenta-works/artifacts/rx6m3rj4_MOMENTA%20STUDIOS%20WHITE.png";

// Bunny iframe video carousel. Bunny provides its own thumbnail, play
// button, and fullscreen support — no separate lightbox is needed. We
// just swap the iframe `src` (with a forced React key to remount) when
// the user steps through prev / next.
const VideoCarousel = ({ videos, projectName }) => {
  const [idx, setIdx] = useState(0);
  const total = videos.length;
  const id = videos[idx];
  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);

  return (
    <div data-testid="video-carousel" className="relative">
      <div className="relative aspect-video w-full bg-black overflow-hidden">
        <iframe
          key={`${id}-${idx}`}
          data-testid={`carousel-iframe-${idx}`}
          src={bunnyEmbed(id, { autoplay: false, muted: true })}
          title={`${projectName} film ${idx + 1}`}
          loading="lazy"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />

        {total > 1 && (
          <>
            <button
              onClick={prev}
              data-testid="carousel-prev"
              aria-label="Previous film"
              className="absolute top-1/2 -translate-y-1/2 left-3 sm:left-5 w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-black/55 hover:bg-black/85 text-white border border-white/40 hover:border-white backdrop-blur transition-all z-10"
            >
              <span className="text-2xl leading-none">←</span>
            </button>
            <button
              onClick={next}
              data-testid="carousel-next"
              aria-label="Next film"
              className="absolute top-1/2 -translate-y-1/2 right-3 sm:right-5 w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-black/55 hover:bg-black/85 text-white border border-white/40 hover:border-white backdrop-blur transition-all z-10"
            >
              <span className="text-2xl leading-none">→</span>
            </button>
          </>
        )}
      </div>

      {total > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <span data-testid="carousel-counter" className="font-mono text-white/70 text-[11px] tracking-[0.25em]">
            {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
          <div className="flex items-center gap-1.5">
            {videos.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`carousel-dot-${i}`}
                aria-label={`Go to film ${i + 1}`}
                className={`h-[2px] transition-all ${
                  i === idx ? "bg-white w-8" : "bg-white/25 w-4 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const CategoryModal = ({ category, onClose }) => {
  const projects = category.projects || [];
  const firstWithMedia =
    projects.find((p) => (p.photos?.length || 0) + (p.videos?.length || 0) > 0) ||
    projects[0];

  const [activeProjectId, setActiveProjectId] = useState(firstWithMedia?.id);
  const activeProject =
    projects.find((p) => p.id === activeProjectId) || firstWithMedia;

  const hasPhotos = (activeProject?.photos?.length || 0) > 0;
  const hasVideos = (activeProject?.videos?.length || 0) > 0;

  const [mediaTab, setMediaTab] = useState(hasPhotos ? PHOTO : VIDEO);
  const [photoLightbox, setPhotoLightbox] = useState(null);
  const scrollRef = useRef(null);

  // Browser back / hardware back closes the modal instead of leaving the site.
  useBackButtonClose(true, onClose, "category-modal");
  // A second sentinel for the photo lightbox so back pops it before the modal.
  useBackButtonClose(Boolean(photoLightbox), () => setPhotoLightbox(null), "photo-lightbox");

  useEffect(() => {
    if (!activeProject) return;
    const ph = activeProject.photos?.length || 0;
    const vi = activeProject.videos?.length || 0;
    if (ph === 0 && vi > 0) setMediaTab(VIDEO);
    else if (vi === 0 && ph > 0) setMediaTab(PHOTO);
  }, [activeProjectId, activeProject]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (photoLightbox) setPhotoLightbox(null);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [photoLightbox, onClose]);

  return (
    <div
      data-testid={`category-modal-${category.id}`}
      ref={scrollRef}
      className="fixed inset-0 z-50 bg-black modal-in overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <button
        onClick={() => {
          onClose();
          setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
        }}
        data-testid="modal-home-logo"
        aria-label="Back to home"
        className="fixed top-4 left-4 sm:top-6 sm:left-10 z-[60] flex items-center bg-black/60 sm:bg-transparent backdrop-blur-sm px-3 py-2 sm:px-0 sm:py-0 hover:opacity-80 transition-opacity"
      >
        <img
          src={MOMENTA_WORDMARK}
          alt="Momenta Studios"
          className="h-7 sm:h-9 w-auto object-contain select-none"
          draggable={false}
        />
      </button>

      <button
        data-testid="category-modal-close"
        onClick={onClose}
        className="fixed top-4 right-4 sm:top-7 sm:right-10 z-[60] text-white text-[11px] tracking-[0.3em] uppercase flex items-center gap-3 hover:opacity-70 bg-black/60 sm:bg-transparent backdrop-blur-sm px-3 py-2 sm:px-0 sm:py-0"
      >
        Close ×
      </button>

      <div className="content-in max-w-[1600px] mx-auto px-5 sm:px-10 lg:px-14 pt-20 sm:pt-28 pb-20 sm:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-10 mb-10 sm:mb-16">
          <div className="md:col-span-7">
            <h2 className="font-serif text-white text-5xl sm:text-6xl lg:text-8xl font-light tracking-tighter leading-[0.9]">
              {category.title}<em className="italic font-medium">.</em>
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex items-end">
            <p className="text-white/60 text-base sm:text-lg font-light leading-relaxed">
              {category.description}
            </p>
          </div>
        </div>

        <div
          data-testid="project-tabs"
          className="flex items-center gap-2 sm:gap-3 overflow-x-auto whitespace-nowrap no-scrollbar border-b border-white/10 pb-4 sm:pb-5 mb-6 sm:mb-10 -mx-5 px-5 sm:mx-0 sm:px-0"
        >
          <span className="eyebrow text-white/40 mr-2 hidden sm:inline shrink-0">Clients /</span>
          {projects.map((p) => {
            const total = (p.photos?.length || 0) + (p.videos?.length || 0);
            return (
              <button
                key={p.id}
                onClick={() => setActiveProjectId(p.id)}
                data-testid={`project-tab-${p.id}`}
                className={`shrink-0 px-3 sm:px-4 py-2 text-[11px] tracking-[0.25em] uppercase border transition-colors ${
                  p.id === activeProjectId
                    ? "bg-white text-black border-white"
                    : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                }`}
              >
                {p.name}
                {total === 0 && <span className="ml-2 text-white/30">soon</span>}
              </button>
            );
          })}
        </div>

        {activeProject && (
          <div data-testid={`project-body-${activeProject.id}`}>
            <div className="flex items-end justify-between mb-5 sm:mb-6 gap-4 flex-wrap">
              <div>
                <div className="eyebrow mb-2">Client</div>
                <h3 className="font-serif italic text-white text-3xl sm:text-4xl lg:text-5xl font-light tracking-tighter">
                  {activeProject.name}
                </h3>
              </div>
              {(hasPhotos || hasVideos) && (
                <div className="eyebrow text-white/40 font-mono">
                  {hasPhotos && `${activeProject.photos.length} photos`}
                  {hasPhotos && hasVideos && " · "}
                  {hasVideos && `${activeProject.videos.length} films`}
                </div>
              )}
            </div>

            {hasPhotos && hasVideos && (
              <div data-testid="media-subtabs" className="flex items-center gap-6 sm:gap-8 border-b border-white/10 mb-8 sm:mb-10">
                {[
                  { id: PHOTO, label: `Photographs · ${activeProject.photos.length}` },
                  { id: VIDEO, label: `Films · ${activeProject.videos.length}` },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setMediaTab(t.id)}
                    data-testid={`subtab-${t.id}`}
                    className={`pb-3 sm:pb-4 text-[11px] tracking-[0.3em] uppercase transition-colors relative ${
                      mediaTab === t.id ? "text-white" : "text-white/40 hover:text-white/70"
                    }`}
                  >
                    {t.label}
                    {mediaTab === t.id && (
                      <span className="absolute left-0 right-0 -bottom-[1px] h-[1px] bg-white" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {!hasPhotos && !hasVideos ? (
              <div className="border border-dashed border-white/15 p-10 sm:p-14 text-center text-white/50 text-sm">
                <div className="font-serif italic text-xl sm:text-2xl text-white/70 mb-3">Gallery coming soon.</div>
                <div>Assets are being uploaded.</div>
              </div>
            ) : mediaTab === PHOTO && hasPhotos ? (
              <div data-testid="project-masonry" className="columns-2 md:columns-3 gap-3 sm:gap-4 max-w-6xl mx-auto">
                {activeProject.photos.map((did, i) => (
                  <button
                    key={did}
                    onClick={() => setPhotoLightbox({ photos: activeProject.photos, index: i, project: activeProject.name })}
                    data-testid={`project-frame-${i}`}
                    className="group relative w-full mb-3 sm:mb-4 overflow-hidden bg-neutral-950 break-inside-avoid block"
                  >
                    <img
                      src={driveThumb(did, 1200)}
                      alt={`${activeProject.name} ${i + 1}`}
                      className="cat-img w-full h-auto block img-noise"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-500" />
                  </button>
                ))}
              </div>
            ) : mediaTab === VIDEO && hasVideos ? (
              <VideoCarousel videos={activeProject.videos} projectName={activeProject.name} />
            ) : null}
          </div>
        )}
      </div>

      {photoLightbox && (
        <div
          data-testid="photo-lightbox"
          className="fixed inset-0 z-[70] bg-black/98 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
          onClick={() => setPhotoLightbox(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setPhotoLightbox(null); }}
            className="absolute top-4 right-4 sm:top-8 sm:right-10 text-white text-[11px] tracking-[0.3em] uppercase z-20"
            data-testid="photo-lightbox-close"
          >
            Close ×
          </button>

          {photoLightbox.photos.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPhotoLightbox((cur) => ({
                    ...cur,
                    index: (cur.index - 1 + cur.photos.length) % cur.photos.length,
                  }));
                }}
                data-testid="photo-lightbox-prev"
                aria-label="Previous photo"
                className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-black/55 hover:bg-black/85 text-white border border-white/40 hover:border-white backdrop-blur transition-all"
              >
                <span className="text-2xl leading-none">←</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPhotoLightbox((cur) => ({
                    ...cur,
                    index: (cur.index + 1) % cur.photos.length,
                  }));
                }}
                data-testid="photo-lightbox-next"
                aria-label="Next photo"
                className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-black/55 hover:bg-black/85 text-white border border-white/40 hover:border-white backdrop-blur transition-all"
              >
                <span className="text-2xl leading-none">→</span>
              </button>
            </>
          )}

          <div className="max-w-6xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              key={photoLightbox.photos[photoLightbox.index]}
              src={driveThumb(photoLightbox.photos[photoLightbox.index], 2000)}
              alt={`${photoLightbox.project} ${photoLightbox.index + 1}`}
              className="max-h-[85vh] w-auto max-w-full object-contain bg-black"
            />
            <div className="mt-3 sm:mt-4 text-white/60 text-[10px] sm:text-[11px] tracking-[0.3em] uppercase font-mono flex items-center gap-3">
              <span>{photoLightbox.project} / {String(photoLightbox.index + 1).padStart(2, "0")}</span>
              <span className="text-white/30">·</span>
              <span>{String(photoLightbox.index + 1).padStart(2, "0")} / {String(photoLightbox.photos.length).padStart(2, "0")}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryModal;
